import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { isObjectsEqual } from '@shared/utils';
import { RegistrationPageService } from './infra/registration-page.service';
import {
  REGISTRATION_CRM_DISABLED,
  type IRegistrationCrmConfig,
  type IRegistrationPage,
} from './entities/registration-page.model';

interface IRegistrationPageState {
  page: IRegistrationPage | null;
}

const initialState: IRegistrationPageState = {
  page: null,
};

/**
 * The gym's default registration link, read through the routes without an id.
 * The counter modal reads its CRM destination from it, for a registration that
 * names no link.
 *
 * A store of its own, next to `RegistrationCandidateStore`: that one lives for
 * the length of a modal and resets on every open.
 */
export const RegistrationPageStore = signalStore(
  { providedIn: 'root' },
  withState<IRegistrationPageState>(initialState),
  withComputed((store) => ({
    /**
     * Compared by content: every answer brings the setting as a new object, and
     * it is new here only when what it says changed.
     */
    crm: computed<IRegistrationCrmConfig>(() => store.page()?.crm ?? REGISTRATION_CRM_DISABLED, {
      equal: isObjectsEqual,
    }),
  })),
  withMethods(
    (store, service = inject(RegistrationPageService), errorStore = inject(ErrorStore)) => ({
      /**
       * The 404 is a gym with no page yet: not a failure, and no toast. The
       * page stays `null`, and `crm` answers CRM off.
       */
      loadPage: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadPage') })),
          switchMap(() =>
            service.getPage().pipe(
              tapResponse({
                next: (page) => patchState(store, { page, ...setPropLoaded('loadPage') }),
                error: (error: HttpErrorResponse) => {
                  if (error.status === 404) {
                    patchState(store, { page: null, ...setPropLoaded('loadPage') });
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('loadPage', error) });
                },
              })
            )
          )
        )
      ),
    })
  ),
  withReqState('loadPage')
);
